import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe } from '@fortawesome/pro-solid-svg-icons';
import PermissionMenu, { type Permission } from 'modules/PermissionMenu';
import { styles } from './styles';
import Button from 'elements/Button';
import { useCallback, useState } from 'react';
import { Box } from 'models/Box';
import useCreateBoxInvitation from 'networking/mindfeed/hooks/useCreateBoxInvitation';
import { InvitationRole } from 'models/Invitation';
import { useTranslations } from 'next-intl';

type Props = {
  box: Box;
};

const LinkInvitations = ({ box }: Props) => {
  const t = useTranslations();
  const createBoxInvitationMutation = useCreateBoxInvitation();
  const [linkPermission, setLinkPermission] = useState<Permission>('edit');
  const [isCopyingLink, setIsCopyingLink] = useState(false);

  const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  };

  const handleCopyLink = useCallback(async (): Promise<boolean> => {
    if (isCopyingLink) return false;

    setIsCopyingLink(true);

    try {
      const nextInvitationRole: InvitationRole =
        linkPermission === 'view' ? 'viewer' : 'editor';
      const invitationLink = await createBoxInvitationMutation.mutateAsync({
        boxId: box.id,
        payload: {
          method: 'link',
          role: nextInvitationRole,
        },
      });

      return await copyToClipboard(invitationLink);
    } catch {
      return false;
    } finally {
      setIsCopyingLink(false);
    }
  }, [box.id, isCopyingLink, linkPermission, createBoxInvitationMutation]);

  return (
    <>
      <p className={styles.title.emphasised}>
        {t('boxSharing.inviteWithLink')}
      </p>

      <div className={styles.sections.link.container}>
        <div className={styles.sections.link.left.container}>
          <div className={styles.sections.link.left.icon} aria-hidden="true">
            <FontAwesomeIcon className={styles.text.muted} icon={faGlobe} />
          </div>
          <p className={styles.sections.link.left.txt}>
            {t('boxSharing.anyoneWithLink')}
          </p>
        </div>

        <div className={styles.sections.link.right}>
          <PermissionMenu
            permission={linkPermission}
            onChange={setLinkPermission}
            showRemove={false}
            showCanPrefix
            showChevronUpWhenOpen
          />
        </div>
      </div>

      <div className={styles.actionsRow}>
        <Button
          type="button"
          variant="outline"
          color="neutral"
          size="sm"
          fullWidth
          onClick={handleCopyLink}
          className="border border-[#BFC4D1] font-semibold text-[14px]"
        >
          {t('boxSharing.copyLink')}
        </Button>
      </div>

      <div className={styles.footer}>
        <p className={styles.footerText}>{t('boxSharing.linkFooter')}</p>
      </div>
    </>
  );
};

export default LinkInvitations;
